import type {
  EntityBillingMember,
  MarginBreakdownResponse,
  MemberEntityAssignment,
  MemberProjectAssignment,
  MemberSkillWithMember,
  MemberWorkloadResponse,
  ProjectBillingMember,
  Skill,
  TeamInsightsMemberRow,
  TeamInsightsMembersPage,
  TeamInsightsSummary,
} from '@/lib/api/member-resources.api'
import {
  entityBillingMemberApi,
  entityJobRoleSelectionApi,
  jobRoleApi,
  memberAllocationApi,
  memberEarningsApi,
  payrollRunApi,
  memberRateApi,
  memberRoleApi,
  memberSkillApi,
  memberUtilizationApi,
  memberWorkloadApi,
  projectBillingMemberApi,
  projectMemberJobRoleApi,
  projectWorkspaceApi,
  teamInsightsApi,
} from '@/lib/api/member-resources.api'
import { useOrganizationStore } from '@/store/organization.store'
import {
  keepPreviousData,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'

import type {
  EnrichedMemberAllocation,
  EntityJobRoleSelection,
  JobRole,
  MemberEarnings,
  MemberOrganizationRole,
  MemberRate,
  MemberSkill,
  MemberUtilization,
  PaginatedResponse,
  ProjectMemberJobRole,
  ProjectOverviewSummaryResponse,
  ProjectStaffingResponse,
  ProjectTimeEntriesResponse,
} from '@scrambl/shared'

import { queryKeys } from '../query-keys'

/**
 * Hook to fetch member rates
 */
export function useMemberRates(
  memberId: string | undefined,
  options?: {
    enabled?: boolean
  },
) {
  const { currentOrganization } = useOrganizationStore()

  return useQuery<MemberRate[]>({
    queryKey: queryKeys.memberResources.rates(memberId || ''),
    queryFn: () => memberRateApi.getByMember(memberId!),
    enabled:
      options?.enabled !== false && !!memberId && !!currentOrganization?.id,
    staleTime: 60 * 1000,
    gcTime: 10 * 60 * 1000,
  })
}

/**
 * Hook to fetch effective rate for a member in a specific context
 */
export function useEffectiveRate(
  params: {
    memberId?: string
    entityId?: string
    projectId?: string
    taskId?: string
    date?: string
  },
  options?: {
    enabled?: boolean
  },
) {
  const { currentOrganization } = useOrganizationStore()

  return useQuery<MemberRate | null>({
    queryKey: queryKeys.memberResources.effectiveRate(params),
    queryFn: () => memberRateApi.getEffectiveRate(params as any),
    enabled:
      options?.enabled !== false &&
      !!params.memberId &&
      !!currentOrganization?.id,
    staleTime: 60 * 1000,
    gcTime: 10 * 60 * 1000,
  })
}

/**
 * Hook to fetch member allocations
 */
export function useMemberAllocations(
  filters?: {
    memberId?: string
    projectId?: string
    entityId?: string
    dealId?: string
    status?: string
    startDate?: string
    endDate?: string
    includeEnrichment?: boolean
  },
  options?: {
    enabled?: boolean
  },
) {
  const { currentOrganization } = useOrganizationStore()

  return useQuery<{ data: EnrichedMemberAllocation[]; total: number }>({
    queryKey: queryKeys.memberResources.allocations(filters),
    queryFn: () => memberAllocationApi.getAll(filters),
    enabled: options?.enabled !== false && !!currentOrganization?.id,
    staleTime: 30 * 1000, // 30 seconds
    gcTime: 5 * 60 * 1000,
  })
}

/**
 * Team-IA Plan 03 — shared-filter wrapper around {@link useMemberAllocations}
 * for the Planner surface's List view.
 *
 * Maps the shared Planner filter context (Zustand `plannerStore`) to the
 * allocation endpoint's param shape:
 *   - `statuses` array → CSV `status` (when a subset is explicitly chosen). When
 *     no statuses are chosen we fetch ALL statuses and let the List view's
 *     client-side reducer apply the "exclude ARCHIVED unless explicitly chosen"
 *     default (this default is intentionally encoded in the view, not here).
 *   - `dateRangeStart/End` → `startDate/endDate` overlap filter.
 *
 * Drilldown (`drilldown*Ids`) is NOT a server param — it is applied in the List
 * view's client-side reducer, so it is not threaded here.
 *
 * The List view mounts this lazily (only when active) so the Schedule view's
 * initial load is unaffected.
 */
export function useSharedAllocations(
  filters: {
    statuses?: string[]
    dateRangeStart?: string
    dateRangeEnd?: string
  },
  options?: { enabled?: boolean },
) {
  const ALL_STATUSES = ['CONFIRMED', 'TENTATIVE', 'PLACEHOLDER', 'ARCHIVED']
  // Subset chosen → narrow server-side; nothing chosen → fetch all and let the
  // view apply the ARCHIVED-by-default exclusion client-side.
  const status =
    filters.statuses && filters.statuses.length > 0
      ? filters.statuses.join(',')
      : ALL_STATUSES.join(',')
  return useMemberAllocations(
    {
      includeEnrichment: true,
      status,
      startDate: filters.dateRangeStart,
      endDate: filters.dateRangeEnd,
    },
    options,
  )
}

/**
 * Hook to fetch allocation conflicts
 * @param memberId - Optional member ID to filter conflicts for a specific member
 */
export function useMemberAllocationConflicts(
  memberId?: string,
  options?: {
    enabled?: boolean
  },
) {
  const { currentOrganization } = useOrganizationStore()

  return useQuery({
    queryKey: queryKeys.memberResources.conflicts(memberId),
    queryFn: () => memberAllocationApi.detectConflicts(memberId),
    enabled: options?.enabled !== false && !!currentOrganization?.id,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
  })
}

/**
 * Hook to fetch member skills
 */
export function useMemberSkills(
  memberId: string | undefined,
  options?: {
    enabled?: boolean
  },
) {
  const { currentOrganization } = useOrganizationStore()

  return useQuery<MemberSkill[]>({
    queryKey: queryKeys.memberResources.skills(memberId || ''),
    queryFn: () => memberSkillApi.getByMember(memberId!),
    enabled:
      options?.enabled !== false && !!memberId && !!currentOrganization?.id,
    staleTime: 60 * 1000,
    gcTime: 10 * 60 * 1000,
  })
}

/**
 * Hook to fetch all member skills across the organization (for skills overview)
 */
export function useAllOrganizationSkills(options?: { enabled?: boolean }) {
  const { currentOrganization } = useOrganizationStore()

  return useQuery<MemberSkillWithMember[]>({
    queryKey: queryKeys.memberResources.allSkills(),
    queryFn: () => memberSkillApi.getAllForOrganization(),
    enabled: options?.enabled !== false && !!currentOrganization?.id,
    staleTime: 60 * 1000,
    gcTime: 10 * 60 * 1000,
  })
}

/**
 * Hook to fetch the organization's skills catalog
 */
export function useSkillsCatalog(options?: { enabled?: boolean }) {
  const { currentOrganization } = useOrganizationStore()

  return useQuery<Skill[]>({
    queryKey: queryKeys.memberResources.skillsCatalog(),
    queryFn: () => memberSkillApi.getCatalog(),
    enabled: options?.enabled !== false && !!currentOrganization?.id,
    staleTime: 5 * 60 * 1000, // 5 minutes - skills catalog doesn't change often
    gcTime: 30 * 60 * 1000,
  })
}

/**
 * Hook to fetch member utilization
 */
export function useMemberUtilization(
  memberId: string | undefined,
  params?: {
    periodType?: 'DAY' | 'WEEK' | 'MONTH'
    startDate?: string
    endDate?: string
  },
  options?: {
    enabled?: boolean
  },
) {
  const { currentOrganization } = useOrganizationStore()

  return useQuery<MemberUtilization[]>({
    queryKey: queryKeys.memberResources.utilization(memberId || '', params),
    queryFn: () => memberUtilizationApi.getByMember(memberId!, params),
    enabled:
      options?.enabled !== false && !!memberId && !!currentOrganization?.id,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
  })
}

/**
 * Hook to fetch team utilization
 */
export function useTeamUtilization(
  params?: {
    periodType?: 'DAY' | 'WEEK' | 'MONTH'
    startDate?: string
    endDate?: string
  },
  options?: {
    enabled?: boolean
  },
) {
  const { currentOrganization } = useOrganizationStore()

  return useQuery<MemberUtilization[]>({
    queryKey: queryKeys.memberResources.teamUtilization(params),
    queryFn: () => memberUtilizationApi.getTeamUtilization(params),
    enabled: options?.enabled !== false && !!currentOrganization?.id,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
  })
}

/**
 * Hook to fetch member earnings
 */
export function useMemberEarnings(
  memberId: string | undefined,
  params?: {
    startDate?: string
    endDate?: string
    status?: string
  },
  options?: {
    enabled?: boolean
  },
) {
  const { currentOrganization } = useOrganizationStore()

  return useQuery<MemberEarnings[]>({
    queryKey: queryKeys.memberResources.earnings(memberId || '', params),
    queryFn: () => memberEarningsApi.getByMember(memberId!, params),
    enabled:
      options?.enabled !== false && !!memberId && !!currentOrganization?.id,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
  })
}

export function useProjectStaffing(
  projectId: string | undefined,
  params?: {
    statusTier?: string
    periodStart?: string
    periodEnd?: string
    includeHistorical?: boolean
  },
  options?: { enabled?: boolean },
) {
  const { currentOrganization } = useOrganizationStore()

  return useQuery<ProjectStaffingResponse>({
    queryKey: queryKeys.projects.staffing(projectId || '', params),
    queryFn: () => projectWorkspaceApi.getStaffing(projectId!, params),
    enabled:
      options?.enabled !== false && !!projectId && !!currentOrganization?.id,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
  })
}

export function useProjectTimeEntries(
  projectId: string | undefined,
  params?: {
    statusTier?: string
    startDate?: string
    endDate?: string
    memberIds?: string[]
    roleIds?: string[]
    workItemType?: 'PROJECT' | 'TASK' | 'TASK_GROUP'
    workItemIds?: string[]
    authorizationId?: string
    billable?: boolean
    status?: string[]
    groupBy?: 'member' | 'week' | 'workItem' | 'role'
    page?: number
    limit?: number
    sortBy?:
      | 'entryDate'
      | 'member'
      | 'role'
      | 'moduleName'
      | 'duration'
      | 'status'
    sortDirection?: 'asc' | 'desc'
  },
  options?: { enabled?: boolean },
) {
  const { currentOrganization } = useOrganizationStore()

  return useQuery<ProjectTimeEntriesResponse>({
    queryKey: queryKeys.projects.timeEntries(projectId || '', params),
    queryFn: () => projectWorkspaceApi.getTimeEntries(projectId!, params),
    enabled:
      options?.enabled !== false && !!projectId && !!currentOrganization?.id,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
    placeholderData: keepPreviousData,
  })
}

export function useProjectOverviewSummary(
  projectId: string | undefined,
  options?: { enabled?: boolean },
) {
  const { currentOrganization } = useOrganizationStore()

  return useQuery<ProjectOverviewSummaryResponse>({
    queryKey: queryKeys.projects.overviewSummary(projectId || ''),
    queryFn: () => projectWorkspaceApi.getOverviewSummary(projectId!),
    enabled:
      options?.enabled !== false && !!projectId && !!currentOrganization?.id,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
  })
}

/**
 * Hook to fetch my earnings (for contractors)
 */
export function useMyEarnings(
  params?: {
    startDate?: string
    endDate?: string
    status?: string
  },
  options?: {
    enabled?: boolean
  },
) {
  const { currentOrganization } = useOrganizationStore()

  return useQuery<MemberEarnings[]>({
    queryKey: queryKeys.memberResources.myEarnings(params),
    queryFn: () => memberEarningsApi.getMyEarnings(params),
    enabled: options?.enabled !== false && !!currentOrganization?.id,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
  })
}

/**
 * Hook to fetch pending earnings
 */
export function usePendingEarnings(options?: { enabled?: boolean }) {
  const { currentOrganization } = useOrganizationStore()

  return useQuery<MemberEarnings[]>({
    queryKey: queryKeys.memberResources.pendingEarnings(),
    queryFn: () => memberEarningsApi.getPending(),
    enabled: options?.enabled !== false && !!currentOrganization?.id,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
  })
}

/**
 * Hook to fetch org-wide earnings totals by status (all periods)
 */
export function useEarningsSummary(options?: { enabled?: boolean }) {
  const { currentOrganization } = useOrganizationStore()

  return useQuery({
    queryKey: [
      ...queryKeys.memberResources.pendingEarnings(),
      'summary',
    ] as const,
    queryFn: () => memberEarningsApi.getSummary(),
    enabled: options?.enabled !== false && !!currentOrganization?.id,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
  })
}

/**
 * Hook to fetch the payroll-run summary for a period
 */
export function usePayrollRunSummary(
  periodStart?: string,
  periodEnd?: string,
  options?: { enabled?: boolean },
) {
  const { currentOrganization } = useOrganizationStore()

  return useQuery({
    queryKey: [
      ...queryKeys.memberResources.pendingEarnings(),
      'payroll-run-summary',
      periodStart,
      periodEnd,
    ] as const,
    queryFn: () => payrollRunApi.getRunSummary(periodStart!, periodEnd!),
    enabled:
      options?.enabled !== false &&
      !!periodStart &&
      !!periodEnd &&
      !!currentOrganization?.id,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
  })
}

export function useAllOrganizationEarnings(
  params?: {
    page?: number
    limit?: number
    startDate?: string
    endDate?: string
    status?: string | string[]
    memberId?: string | string[]
    employmentType?: string
    search?: string
    sortBy?: string
    sortDir?: string
  },
  options?: { enabled?: boolean },
) {
  const { currentOrganization } = useOrganizationStore()

  return useQuery<PaginatedResponse<MemberEarnings>>({
    queryKey: [
      ...queryKeys.memberResources.pendingEarnings(),
      'all',
      params,
    ] as const,
    queryFn: () => memberEarningsApi.getAll(params),
    enabled: options?.enabled !== false && !!currentOrganization?.id,
    placeholderData: keepPreviousData,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
  })
}

/**
 * Hook to fetch a member's assigned organization roles
 */
export function useMemberRoles(
  memberId: string | undefined,
  options?: {
    enabled?: boolean
  },
) {
  const { currentOrganization } = useOrganizationStore()

  return useQuery<MemberOrganizationRole[]>({
    queryKey: queryKeys.memberResources.memberRoles(memberId || ''),
    queryFn: () => memberRoleApi.getByMember(memberId!),
    enabled:
      options?.enabled !== false && !!memberId && !!currentOrganization?.id,
    staleTime: 60 * 1000,
    gcTime: 10 * 60 * 1000,
  })
}

/**
 * Hook to fetch all project assignments for a member across all projects,
 * including each project's assigned job roles and effective rate card bill rates.
 */
export function useMemberProjectAssignments(
  memberId: string | undefined,
  options?: {
    enabled?: boolean
  },
) {
  const { currentOrganization } = useOrganizationStore()

  return useQuery<MemberProjectAssignment[]>({
    queryKey: ['member-project-assignments', memberId || ''],
    queryFn: () => memberRoleApi.getProjectAssignments(memberId!),
    enabled:
      options?.enabled !== false && !!memberId && !!currentOrganization?.id,
    staleTime: 60 * 1000,
    gcTime: 10 * 60 * 1000,
  })
}

/**
 * Hook to fetch a member's direct client-level billing assignments.
 */
export function useMemberEntityAssignments(
  memberId: string | undefined,
  options?: {
    enabled?: boolean
  },
) {
  const { currentOrganization } = useOrganizationStore()

  return useQuery<MemberEntityAssignment[]>({
    queryKey: ['member-entity-assignments', memberId || ''],
    queryFn: () => memberRoleApi.getEntityAssignments(memberId!),
    enabled:
      options?.enabled !== false && !!memberId && !!currentOrganization?.id,
    staleTime: 60 * 1000,
    gcTime: 10 * 60 * 1000,
  })
}

/**
 * Hook to fetch the resolved billing roster for a single project.
 */
export function useProjectBillingMembers(
  projectId: string | undefined,
  options?: {
    enabled?: boolean
  },
) {
  const { currentOrganization } = useOrganizationStore()

  return useQuery<ProjectBillingMember[]>({
    queryKey: ['project-billing-members', projectId || ''],
    queryFn: () => projectBillingMemberApi.list(projectId!),
    enabled:
      options?.enabled !== false && !!projectId && !!currentOrganization?.id,
    staleTime: 60 * 1000,
    gcTime: 10 * 60 * 1000,
  })
}

/**
 * Hook to fetch the entity-level billing roster (who bills against this client).
 */
export function useEntityBillingMembers(
  entityId: string | undefined,
  options?: {
    enabled?: boolean
  },
) {
  const { currentOrganization } = useOrganizationStore()

  return useQuery<EntityBillingMember[]>({
    queryKey: ['entity-billing-members', entityId || ''],
    queryFn: () => entityBillingMemberApi.list(entityId!),
    enabled:
      options?.enabled !== false && !!entityId && !!currentOrganization?.id,
    staleTime: 60 * 1000,
    gcTime: 10 * 60 * 1000,
  })
}

/**
 * Hook to fetch available roles for a member in a specific context (cascading scoping)
 */
export function useAvailableRoles(
  params: {
    memberId?: string
    projectId?: string | null
    entityId?: string | null
  },
  options?: {
    enabled?: boolean
  },
) {
  const { currentOrganization } = useOrganizationStore()

  return useQuery<MemberOrganizationRole[]>({
    queryKey: queryKeys.memberResources.availableRoles({
      memberId: params.memberId,
      projectId: params.projectId,
      entityId: params.entityId,
    }),
    queryFn: () =>
      memberRoleApi.getAvailable({
        memberId: params.memberId!,
        projectId: params.projectId,
        entityId: params.entityId,
      }),
    enabled:
      options?.enabled !== false &&
      !!params.memberId &&
      !!currentOrganization?.id,
    staleTime: 60 * 1000,
    gcTime: 10 * 60 * 1000,
  })
}

/**
 * Hook to fetch all organization job roles
 */
export function useJobRoles(options?: { enabled?: boolean }) {
  const { currentOrganization } = useOrganizationStore()

  return useQuery<JobRole[]>({
    queryKey: queryKeys.memberResources.jobRoles(),
    queryFn: () => jobRoleApi.getAll(),
    enabled: options?.enabled !== false && !!currentOrganization?.id,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  })
}

/**
 * Hook to fetch entity-level job role selections
 */
export function useEntityRoleSelections(
  entityId: string | undefined,
  options?: {
    enabled?: boolean
  },
) {
  const { currentOrganization } = useOrganizationStore()

  return useQuery<EntityJobRoleSelection[]>({
    queryKey: queryKeys.memberResources.entityRoleSelections(entityId || ''),
    queryFn: () => entityJobRoleSelectionApi.get(entityId!),
    enabled:
      options?.enabled !== false && !!entityId && !!currentOrganization?.id,
    staleTime: 60 * 1000,
    gcTime: 10 * 60 * 1000,
  })
}

/**
 * Hook to fetch project-member job role assignments
 */
export function useProjectMemberRoles(
  projectId: string | undefined,
  memberId: string | undefined,
  options?: {
    enabled?: boolean
  },
) {
  const { currentOrganization } = useOrganizationStore()

  return useQuery<ProjectMemberJobRole[]>({
    queryKey: queryKeys.memberResources.projectMemberRoles(
      projectId || '',
      memberId || '',
    ),
    queryFn: () => projectMemberJobRoleApi.get(projectId!, memberId!),
    enabled:
      options?.enabled !== false &&
      !!projectId &&
      !!memberId &&
      !!currentOrganization?.id,
    staleTime: 60 * 1000,
    gcTime: 10 * 60 * 1000,
  })
}

export function useMemberWorkload(
  memberId: string | undefined,
  params?: { period?: string },
  options?: {
    enabled?: boolean
  },
) {
  const { currentOrganization } = useOrganizationStore()

  return useQuery<MemberWorkloadResponse>({
    queryKey: queryKeys.memberResources.workload(
      memberId || '',
      params?.period,
    ),
    queryFn: () => memberWorkloadApi.getWorkload(memberId!, params),
    enabled:
      options?.enabled !== false && !!memberId && !!currentOrganization?.id,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
  })
}

export function useAllocationHistory(
  allocationId: string | undefined,
  options?: {
    enabled?: boolean
  },
) {
  const { currentOrganization } = useOrganizationStore()

  return useQuery({
    queryKey: queryKeys.memberResources.allocationHistory(allocationId || ''),
    queryFn: () => memberAllocationApi.getHistory(allocationId!),
    enabled:
      options?.enabled !== false && !!allocationId && !!currentOrganization?.id,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
  })
}

// Plan 9b: Combined Team Insights hook — one request, populates individual caches
export function useTeamInsights(options?: { enabled?: boolean }) {
  const { currentOrganization } = useOrganizationStore()
  const queryClient = useQueryClient()
  return useQuery({
    queryKey: queryKeys.memberResources.insights(),
    queryFn: async () => {
      const data = await teamInsightsApi.getAll()
      // Populate individual caches so components using the sub-hooks read from cache
      queryClient.setQueryData(
        queryKeys.memberResources.insightsSummary(),
        data.summary,
      )
      queryClient.setQueryData(
        queryKeys.memberResources.insightsMembers(),
        data.members,
      )
      return data
    },
    enabled: options?.enabled !== false && !!currentOrganization?.id,
    staleTime: 60 * 1000,
  })
}

// Plan 9b: Team Insights hooks
export function useTeamInsightsMembers(
  params?: {
    sortBy?:
      | 'burnoutRisk'
      | 'billableUtilization'
      | 'totalUtilization'
      | 'weightedMargin'
  },
  options?: { enabled?: boolean },
) {
  const { currentOrganization } = useOrganizationStore()
  const RISK_ORDER: Record<string, number> = {
    HIGH: 3,
    ELEVATED: 2,
    WATCH: 1,
    NONE: 0,
  }
  return useQuery<TeamInsightsMemberRow[]>({
    queryKey: queryKeys.memberResources.insightsMembers(),
    queryFn: () => teamInsightsApi.getMembers(),
    enabled: options?.enabled !== false && !!currentOrganization?.id,
    staleTime: 60 * 1000,
    select: data => {
      const sortBy = params?.sortBy ?? 'burnoutRisk'
      const sorted = [...data]
      if (sortBy === 'burnoutRisk') {
        sorted.sort(
          (a, b) =>
            RISK_ORDER[b.burnoutRisk?.level ?? 'NONE'] -
            RISK_ORDER[a.burnoutRisk?.level ?? 'NONE'],
        )
      } else if (sortBy === 'billableUtilization') {
        sorted.sort((a, b) => b.billableUtilization - a.billableUtilization)
      } else if (sortBy === 'totalUtilization') {
        sorted.sort((a, b) => b.totalUtilization - a.totalUtilization)
      } else if (sortBy === 'weightedMargin') {
        sorted.sort(
          (a, b) =>
            (b.weightedMarginPct ?? -Infinity) -
            (a.weightedMarginPct ?? -Infinity),
        )
      }
      return sorted
    },
  })
}

/** Server-paginated insights members (sort/group/search/paginate all server-side). */
export function useTeamInsightsMembersPaged(
  params: {
    sortBy?: 'burnoutRisk' | 'billableUtilization' | 'totalUtilization' | 'weightedMargin'
    groupBy?: 'none' | 'department' | 'role'
    search?: string
    page?: number
    pageSize?: number
  },
  options?: { enabled?: boolean },
) {
  const { currentOrganization } = useOrganizationStore()
  return useQuery<TeamInsightsMembersPage>({
    queryKey: queryKeys.memberResources.insightsMembers({
      sortBy: params.sortBy ?? 'burnoutRisk',
      groupBy: params.groupBy ?? 'none',
      search: params.search ?? '',
      page: String(params.page ?? 1),
      pageSize: String(params.pageSize ?? 25),
      paged: '1',
    }),
    queryFn: () => teamInsightsApi.getMembersPaged(params),
    enabled: options?.enabled !== false && !!currentOrganization?.id,
    staleTime: 60 * 1000,
    placeholderData: prev => prev,
  })
}

export function useTeamInsightsSummary(options?: { enabled?: boolean }) {
  const { currentOrganization } = useOrganizationStore()
  return useQuery<TeamInsightsSummary>({
    queryKey: queryKeys.memberResources.insightsSummary(),
    queryFn: () => teamInsightsApi.getSummary(),
    enabled: options?.enabled !== false && !!currentOrganization?.id,
    staleTime: 60 * 1000,
  })
}

// Plan 9c T16: Per-member margin breakdown by project
export function useMemberMarginBreakdown(
  memberId: string | null | undefined,
  params?: { periodStart?: string; periodEnd?: string },
  options?: { enabled?: boolean },
) {
  const { currentOrganization } = useOrganizationStore()
  return useQuery<MarginBreakdownResponse>({
    queryKey: queryKeys.memberResources.insightsMarginBreakdown(
      memberId ?? '',
      params as Record<string, string>,
    ),
    queryFn: () => teamInsightsApi.getMarginBreakdown(memberId!, params),
    enabled:
      options?.enabled !== false && !!currentOrganization?.id && !!memberId,
    staleTime: 60 * 1000,
  })
}
